import {
  CheckmarkIcon,
  RouteIcon,
  SaveIcon,
  WarningIcon,
} from '@moia-dev/pace-icons'
import {
  Button,
  LoadingIndicator,
  Text,
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from '@moia-dev/pace-web'
import { useRef } from 'react'
import { useTranslation } from 'react-i18next'
import { aggregateOddElements, getOddTypeLabel } from '../utils'
import type {
  AssigningVehicle,
  MobileyeStatus,
  ODDMapElement,
  SdsViolation,
  ValidatedRoute,
} from './types'

const TRANSITION_MS = 280

const FAILURE_REASON_LABELS: Record<string, string> = {
  INVALID: 'Unknown',
  NO_MAP_COVERAGE: 'No Map Coverage',
  SEGMENT_TOO_SHORT: 'Segment Too Short',
  SEGMENT_GAP: 'Undrivable connection between segments',
}

const formatDistanceMiles = (meters: number) => {
  const miles = meters * 0.000621371
  if (miles < 0.1) return '< 0.1 mi'
  return `${Math.round(miles * 10) / 10} mi`
}

const styles = {
  row: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'baseline',
  },
} as const

type GeneratedRouteSummaryProps = {
  validatedRoute: ValidatedRoute
  oddElements: ODDMapElement[]
  fetchingOdds?: boolean
  mobileyeStatus: MobileyeStatus
  validatingSds: boolean
  sdsViolations: SdsViolation[]
  validatedMapName?: string | null
  fleetId: string
  isSaving: boolean
  hasViolations: boolean
  onMobileyeValidation: () => void
  onSave: () => void
  /** When provided, replaces the Save button with an Assign to Vehicle button. */
  assigningVehicle?: AssigningVehicle | null
  onAssign?: () => void
  assigning?: boolean
}

export const GeneratedRouteSummary = ({
  validatedRoute,
  oddElements,
  fetchingOdds = false,
  mobileyeStatus,
  validatingSds,
  sdsViolations,
  validatedMapName,
  fleetId,
  isSaving,
  hasViolations,
  onMobileyeValidation,
  onSave,
  assigningVehicle,
  onAssign,
  assigning = false,
}: GeneratedRouteSummaryProps) => {
  const { t } = useTranslation()
  const sortedOdds = aggregateOddElements(oddElements)
  const hasOdds = sortedOdds.length > 0
  // Keep the last non-empty list so content stays during collapse animation
  const lastSortedOddsRef = useRef(sortedOdds)
  if (hasOdds) lastSortedOddsRef.current = sortedOdds
  const displaySortedOdds = lastSortedOddsRef.current

  return (
    <div
      css={theme => ({
        flexShrink: 0,
        borderTop: `1px solid ${theme.semantic.ColorAccessoryReduced}`,
        backgroundColor: theme.semantic.ColorSurfaceDefault,
      })}
    >
      {/* Stats section */}
      <div
        css={theme => ({
          padding: `${theme.space.Space3} ${theme.space.Space4}`,
          display: 'flex',
          flexDirection: 'column',
          gap: theme.space.Space1,
        })}
      >
        <div css={styles.row}>
          <Text size="sm" weight="bold">
            Distance
          </Text>
          <Text size="sm" weight="bold">
            {formatDistanceMiles(validatedRoute.totalDistanceMeters)}
          </Text>
        </div>

        <div css={styles.row}>
          <Text size="sm" weight="bold">
            Total Estimated Time
          </Text>
          <Text size="sm" weight="bold">
            {Math.ceil(validatedRoute.totalEtaSeconds / 60)} min
          </Text>
        </div>

        {/* ODD fetch loading indicator */}
        {fetchingOdds && !hasOdds && (
          <div css={styles.row}>
            <Text size="sm" color="reduced">
              Fetching ODD elements…
            </Text>
            <LoadingIndicator size={16} />
          </div>
        )}

        {/* ODD counts — animate in/out when odds appear or disappear */}
        <div
          css={{
            display: 'grid',
            gridTemplateRows: hasOdds ? '1fr' : '0fr',
            transition: `grid-template-rows ${TRANSITION_MS}ms ease`,
          }}
        >
          <div
            css={{
              overflow: 'hidden',
              opacity: hasOdds ? 1 : 0,
              transition: `opacity ${TRANSITION_MS}ms ease`,
              display: 'flex',
              flexDirection: 'column',
              gap: 4,
            }}
          >
            {displaySortedOdds.map(([type, count]) => (
              <div key={type} css={styles.row}>
                <Text size="sm" color="reduced">
                  {getOddTypeLabel(type, t)}
                </Text>
                <Text size="sm" color="reduced">
                  {count}
                </Text>
              </div>
            ))}
          </div>
        </div>

        {sdsViolations.length > 0 && (
          <div
            css={theme => ({
              marginTop: theme.space.Space1,
              display: 'flex',
              flexDirection: 'column',
              gap: theme.space.Space1,
            })}
          >
            {sdsViolations.map(v => (
              <Text
                size="sm"
                key={`sds-${v.startIndex}-${v.endIndex}-${v.reason}`}
                css={theme => ({ color: theme.semantic.ColorCriticalDefault })}
              >
                {FAILURE_REASON_LABELS[v.reason] ?? v.reason}
                {v.mapName ? ` (${v.mapName})` : ''}
                {v.message ? ` — ${v.message}` : ''}
              </Text>
            ))}
          </div>
        )}
      </div>

      {/* Action row */}
      <div
        css={theme => ({
          padding: `${theme.space.Space3} ${theme.space.Space4}`,
          borderTop: `1px solid ${theme.semantic.ColorAccessoryReduced}`,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          gap: theme.space.Space3,
        })}
      >
        <Tooltip>
          <TooltipTrigger>
            <div>
              <Button
                label={
                  validatingSds
                    ? 'Validating...'
                    : mobileyeStatus === 'passed'
                      ? 'Mobileye: Passed'
                      : mobileyeStatus === 'failed'
                        ? 'Mobileye: Failed'
                        : 'Mobileye Validation'
                }
                variant="plain"
                size="compact"
                onClick={onMobileyeValidation}
                disabled={validatingSds || hasViolations || !fleetId}
                icon={
                  validatingSds ? (
                    <LoadingIndicator size={16} />
                  ) : mobileyeStatus === 'passed' ? (
                    <CheckmarkIcon />
                  ) : mobileyeStatus === 'failed' ? (
                    <WarningIcon />
                  ) : undefined
                }
                css={theme => ({
                  ...(validatingSds || hasViolations
                    ? {}
                    : mobileyeStatus === 'passed'
                      ? { color: theme.semantic.ColorSuccessDefault }
                      : mobileyeStatus === 'failed'
                        ? { color: theme.semantic.ColorCriticalDefault }
                        : {}),
                })}
              />
            </div>
          </TooltipTrigger>
          {(hasViolations || !fleetId || validatedMapName) && (
            <TooltipContent>
              {hasViolations
                ? 'Resolve all route violations before validating'
                : !fleetId
                  ? 'No fleet configured for selected metropolis'
                  : `Map: ${validatedMapName?.split('_').slice(-3).join('_')}`}
            </TooltipContent>
          )}
        </Tooltip>

        {assigningVehicle ? (
          <Tooltip>
            <TooltipTrigger>
              <div>
                <Button
                  label={assigning ? 'Assigning...' : 'Assign'}
                  variant="plain"
                  size="compact"
                  onClick={onAssign}
                  disabled={assigning || hasViolations}
                  icon={
                    assigning ? <LoadingIndicator size={16} /> : <RouteIcon />
                  }
                />
              </div>
            </TooltipTrigger>
            {hasViolations && (
              <TooltipContent>
                Resolve all route violations before assigning
              </TooltipContent>
            )}
          </Tooltip>
        ) : (
          <Tooltip>
            <TooltipTrigger>
              <div>
                <Button
                  label={isSaving ? 'Saving...' : 'Save Route'}
                  variant="plain"
                  size="compact"
                  onClick={onSave}
                  disabled={isSaving || hasViolations}
                  icon={<SaveIcon />}
                />
              </div>
            </TooltipTrigger>
            {hasViolations && (
              <TooltipContent>
                Resolve all route violations before saving
              </TooltipContent>
            )}
          </Tooltip>
        )}
      </div>
    </div>
  )
}
